(ns common.google-analytics
  (:require ["moment-timezone" :as moment-tz]
            [clojure.string :as str]
            [common.os :as os]
            [goog.crypt :as crypt]
            [goog.crypt.Sha256]
            [goog.crypt.base64 :as base64]
            [re-frame.core :as rf]))

;; Configuration
(def ^:private gtm-id "GTM-56M5TDZ9") ; GTM ID for Media Studio
(def ^:private data-layer-name "wppopen_dataLayer")

;; JWT Decoding

(defn base64-url-decode
  [s]
  (let [base64-str (-> s
                       (str/replace "-" "+")
                       (str/replace "_" "/"))
        padding (mod (- 4 (mod (count base64-str) 4)) 4)
        padded (str base64-str (apply str (repeat padding "=")))]

    (base64/decodeString padded)))

(defn decode-jwt-payload
  "Decode JWT token and return the payload as a NATIVE Javascript Object"
  [token]
  (when (string? token)
    (try
      (let [parts (str/split token #"\.")]
        (if (< (count parts) 2)
          nil
          (let [payload-str (base64-url-decode (nth parts 1))]
            (js/JSON.parse payload-str))))
      (catch :default _
        nil))))

(defn extract-user-data-from-token
  [token]
  (when-let [payload (decode-jwt-payload token)]
    (let [payload-map (js->clj payload :keywordize-keys true)
          extracted {:name (:name payload-map)
                     :email (:email payload-map)
                     :company (:company payload-map)
                     :country-code (or (:countryCode payload-map) (:country-code payload-map))
                     :department (:department payload-map)
                     :title (:title payload-map)
                     :city (:city payload-map)
                     :geo-region (or (:geoRegion payload-map) (:geo-region payload-map))
                     :external-user (or (:externalUser payload-map) (:external-user payload-map))}]
      extracted)))

(defn- ensure-wpp-data-layer!
  []
  (when-not (aget js/window data-layer-name)
    (aset js/window data-layer-name #js [])))

(defn- convert-nils-to-undefined
  "Recursively converts nil values to js/undefined in the resulting JS object"
  [m]
  (let [js-obj #js {}]
    (doseq [[k v] m]
      (let [js-key (if (keyword? k) (name k) (str k))
            js-val (cond
                     (nil? v) js/undefined
                     (map? v) (convert-nils-to-undefined v)
                     :else (clj->js v))]
        (aset js-obj js-key js-val)))
    js-obj))

(defn- push-to-data-layer!
  [event-data]
  (ensure-wpp-data-layer!)
  (let [data-layer (aget js/window data-layer-name)
        js-data (convert-nils-to-undefined event-data)]
    (.push data-layer js-data)))

(defn get-user-id-hash [db]
  (get db :ga4/user-id-hash))

;; GTM Script Loader
(defn- create-gtm-script
  "Creates GTM script element with WPP Open configuration"
  [gtm-id]
  (str "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':"
       "new Date().getTime(),event:'gtm.js'});var "
       "f=d.getElementsByTagName(s)[0],"
       "j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';"
       "j.async=true;j.src="
       "'https://www.googletagmanager.com/gtm.js?id='+i+dl;"
       "f.parentNode.insertBefore(j,f);"
       "})(window,document,'script','" data-layer-name "','" gtm-id "');"))

(rf/reg-fx
  :ga4/load-script
  (fn [{:keys [gtm-id]}]
    (let [script (js/document.createElement "script")
          head (.-head js/document)]
      (set! (.-innerHTML script) (create-gtm-script gtm-id))
      (.appendChild head script))))

(rf/reg-fx
  :ga4/push-to-data-layer!
  (fn [event-data]
    (push-to-data-layer! event-data)))

;; Standard Data Object Structure
(defn create-base-data-object
  [& {:keys [user-data workspace-data virtual-page-data application-data environment]}]
  (merge {:timestamp (.getTime (js/Date.))
          :environment environment}
         user-data
         workspace-data
         application-data
         virtual-page-data))

;; Helper functions for user data mapping

(defn get-timezone-info
  "Gets current timezone and resolves it to location information using moment-timezone"
  []
  (let [timezone (-> (js/Intl.DateTimeFormat.) (.resolvedOptions) .-timeZone)]
    (try
      (let [countries (-> (.. moment-tz -tz)
                          (.zone timezone)
                          (.countries))
            country-code (first countries)
            [region city] (str/split timezone #"/")]
        {:timezone timezone
         :country-code country-code
         :region region
         :city (when city (str/replace city "_" " "))})
      (catch js/Object _
        {:timezone timezone
         :country-code nil
         :region nil
         :city nil}))))

(defn create-user-id-hash
  "Creates a user ID hash from email"
  [email]
  (when email
    (let [normalized-email (-> email
                               (str/trim)
                               (str/lower-case))
          hasher (goog.crypt.Sha256.)]
      (.update hasher (crypt/stringToByteArray normalized-email))
      (crypt/byteArrayToHex (.digest hasher)))))

(defn map-user-data-for-ga4-from-roles
  "Maps user data with roles array to GA4 format (for OS context or user object with roles)"
  [user-data]
  (let [timezone-info (get-timezone-info)
        first-role (first (:roles user-data))]
    {:user_company (get-in first-role [:agencyName])
     :user_country_code (:country-code timezone-info)
     :user_department nil
     :user_job_role (get-in first-role [:roleName])
     :user_region (:region timezone-info)
     :user_is_external (not (:ssoEnabled user-data))}))

(defn map-user-data-for-ga4-from-token
  "Maps token-extracted user data to GA4 format (for standalone mode)"
  [token-data]
  (let [timezone-info (get-timezone-info)]
    {:user_company (:company token-data)
     :user_country_code (or (:country-code token-data) (:country-code timezone-info))
     :user_department (:department token-data)
     :user_job_role (:title token-data)
     :user_region (or (:geo-region token-data) (:region timezone-info))
     :user_is_external (boolean (:external-user token-data))}))

;; Mandatory Events

(rf/reg-event-fx
  :ga4/track-user-login
  [(rf/inject-cofx :sessionstorage ["sso-id-token"])]
  (fn [{:keys [db] :sessionstorage/keys [sso-id-token]} [_ user-data]]
    (let [os-environment? (os/os-environment?)

          ;; Try to get token data if not in OS context
          token-data (when (and (not os-environment?) sso-id-token)
                       (extract-user-data-from-token sso-id-token))

          mapped-user-data (cond
                             ;; If we have token data, use it
                             token-data
                             (map-user-data-for-ga4-from-token token-data)

                             ;; If user-data has roles, map from roles
                             (and user-data (seq (:roles user-data)))
                             (map-user-data-for-ga4-from-roles user-data)

                             ;; Otherwise use what's in db
                             :else
                             (get db :ga4/user-data))

          user-email (or (:email user-data)
                         (:email token-data))
          user-id-hash (when user-email (create-user-id-hash user-email))

          final-user-data (or mapped-user-data (get db :ga4/user-data))
          final-user-id-hash (or user-id-hash (get-user-id-hash db))
          environment (get db :ga4/environment)

          base-data (create-base-data-object
                     :user-data final-user-data
                     :environment environment)

          login-event (merge base-data
                             {:event "user_login"
                              :user_id final-user-id-hash})]

      {:db (cond-> db
             user-id-hash (assoc :ga4/user-id-hash user-id-hash)
             mapped-user-data (assoc :ga4/user-data mapped-user-data))
       :fx [(when-not os-environment?
              [:ga4/push-to-data-layer! login-event])]})))

(rf/reg-event-fx
  :ga4/track-page-view
  (fn [{:keys [db]} [_ workspace-data]]
    (let [ws-data (or workspace-data (get db :ga4/workspace-data))
          environment (get db :ga4/environment)
          base-data (create-base-data-object
                     :workspace-data ws-data
                     :environment environment)

          page-view-event (merge base-data
                                 {:event "page_view"
                                  :user_id (get-user-id-hash db)})]

      {:db db
       :ga4/push-to-data-layer! page-view-event})))

(rf/reg-event-fx
  :ga4/track-virtual-page-view
  (fn [{:keys [db]} [_ virtual-page-data]]
    (when-let [user-id-hash (get-user-id-hash db)]
      (let [ws-data (get db :ga4/workspace-data)
            project-name (get db :ga4/project-name)
            previous-url (get db :ga4/previous-url)
            current-url (or (:pageUrl virtual-page-data) (.-href js/window.location))

            virtual-page-fields {:pageUrl current-url
                                 :pageTitle (or (:pageTitle virtual-page-data) (.-title js/document))
                                 :previousUrl previous-url
                                 :project project-name}

            environment (get db :ga4/environment)
            base-data (create-base-data-object
                       :workspace-data ws-data
                       :virtual-page-data virtual-page-fields
                       :environment environment)

            virtual-page-event (merge base-data
                                      {:event "virtual_page_view"
                                       :user_id user-id-hash})]

        {:db (assoc db :ga4/previous-url current-url)
         :ga4/push-to-data-layer! virtual-page-event}))))

(rf/reg-event-fx
  :ga4/track-application-launch
  (fn [{:keys [db]} [_ application-data workspace-data]]
    (let [app-data (or application-data (get db :ga4/application-data))
          ws-data (or workspace-data (get db :ga4/workspace-data))
          project-name (get db :ga4/project-name)
          environment (get db :ga4/environment)
          base-data (create-base-data-object
                     :application-data app-data
                     :workspace-data ws-data
                     :environment environment)

          launch-event (merge base-data
                              {:event "app_launch"
                               :user_id (get-user-id-hash db)
                               :project project-name})]

      {:db db
       :ga4/push-to-data-layer! launch-event})))

;; Optional Events

(rf/reg-event-fx
  :ga4/track-user-interaction
  (fn [{:keys [db]} [_ application-data workspace-data interaction-data]]
    (let [app-data (or application-data (get db :ga4/application-data))
          ws-data (or workspace-data (get db :ga4/workspace-data))
          environment (get db :ga4/environment)
          base-data (create-base-data-object
                     :application-data app-data
                     :workspace-data ws-data
                     :environment environment)

          interaction-event (merge base-data
                                   {:event (:eventType interaction-data)
                                    :user_id (get-user-id-hash db)}
                                   (dissoc interaction-data :eventType))]

      {:db db
       :ga4/push-to-data-layer! interaction-event})))

(rf/reg-event-fx
  :ga4/track-feature-usage
  (fn [{:keys [db]} [_ application-data workspace-data feature-data]]
    (let [app-data (or application-data (get db :ga4/application-data))
          ws-data (or workspace-data (get db :ga4/workspace-data))
          environment (get db :ga4/environment)
          base-data (create-base-data-object
                     :application-data app-data
                     :workspace-data ws-data
                     :environment environment)

          feature-event (merge base-data
                               {:event (:eventType feature-data)
                                :user_id (get-user-id-hash db)}
                               (dissoc feature-data :eventType))]

      {:db db
       :ga4/push-to-data-layer! feature-event})))

;; Initialization

(rf/reg-event-fx
  :ga4/initialize-with-os-context
  [(rf/inject-cofx :env)]
  (fn [{:keys [db env]} [_ {:keys [osContext]}]]

    (let [environment (str "co-" env)
          os-user-data (:userDetails osContext)
          os-hierarchy (:hierarchy osContext)
          os-tenant (:tenant osContext)
          os-project (:project osContext)
          os-app-instance (:appInstance osContext)
          app-inst-devhubmeta (:devhubMetadata os-app-instance)

          user-id-hash (when (:email os-user-data)
                         (create-user-id-hash (:email os-user-data)))

          user-data {:user_company (:company os-user-data)
                     :user_country_code (:countryAlpha2Code os-user-data)
                     :user_department (:department os-user-data)
                     :user_job_role (:jobTitle os-user-data)
                     :user_region (:geoRegion os-user-data)
                     :user_is_external (:isExternal os-user-data)}


          hierarchy-mapping (:mapping os-hierarchy)
          find-by-type (fn [type-name]
                         (some (fn [[_k {:keys [type name]}]]
                                 (when (= (some-> type str/lower-case)
                                          (str/lower-case type-name))
                                   name))
                               hierarchy-mapping))

          workspace-data {:workspace_market (find-by-type "Market")
                          :workspace_industry (find-by-type "Industry")
                          :workspace_brand (find-by-type "Brand")
                          :workspace_client (find-by-type "Client")
                          :workspace_region (find-by-type "Region")
                          :workspace_custom (find-by-type "Custom")
                          :context_type "os"
                          :tenant_name (:name os-tenant)}

          project-name (:name os-project)

          application-data {:application_id (:devhubAppId os-app-instance)
                            :application_instance_id (:id os-app-instance)
                            :product_type (:productType app-inst-devhubmeta)
                            :nocode_apptype (:noCodeAppType app-inst-devhubmeta)
                            :product_name "Creative Optimizations"
                            :product_category "Media"
                            :product_subcategory "Activate"}]

      {:db (assoc db :ga4/user-id-hash user-id-hash
                  :ga4/application-data application-data
                  :ga4/workspace-data workspace-data
                  :ga4/project-name project-name
                  :ga4/user-data user-data
                  :ga4/environment environment
                  :ga4/previous-url nil)

       :ga4/load-script {:gtm-id gtm-id}
       :dispatch-later [{:ms 0 :dispatch [:ga4/track-page-view]}
                        {:ms 100 :dispatch [:ga4/track-application-launch]}]})))

(rf/reg-event-fx
  :ga4/initialize-without-context
  [(rf/inject-cofx :env)
   (rf/inject-cofx :sessionstorage ["sso-id-token"])]
  (fn [{:keys [db env] :sessionstorage/keys [sso-id-token]}]
    (let [environment (str "co-" env)

          token-data (extract-user-data-from-token sso-id-token)

          user-email (when token-data (:email token-data))

          user-id-hash (when user-email
                         (create-user-id-hash user-email))

          user-data (if (and token-data (seq token-data))
                      (map-user-data-for-ga4-from-token token-data)
                      {:user_company nil
                       :user_country_code nil
                       :user_department nil
                       :user_job_role nil
                       :user_region nil
                       :user_is_external false})

          workspace-data {:workspace_market nil
                          :workspace_industry nil
                          :workspace_brand nil
                          :workspace_client nil
                          :workspace_region nil
                          :workspace_custom nil
                          :context_type "standalone"
                          :tenant_name nil}

          application-data {:application_id nil
                            :application_instance_id nil
                            :product_type nil
                            :nocode_apptype nil
                            :product_name "Creative Optimizations"
                            :product_category "Media"
                            :product_subcategory "Activate"}

          project-name nil]

      {:db (assoc db :ga4/user-id-hash user-id-hash
                  :ga4/application-data application-data
                  :ga4/workspace-data workspace-data
                  :ga4/project-name project-name
                  :ga4/user-data user-data
                  :ga4/environment environment
                  :ga4/previous-url nil)

       :ga4/load-script {:gtm-id gtm-id}
       :dispatch-later [{:ms 0 :dispatch [:ga4/track-page-view]}
                        {:ms 100 :dispatch [:ga4/track-application-launch]}]})))

(defn web-init-ga4!
  "Initialize Web Google Analytics 4 tracking"
  []
  (rf/dispatch [:ga4/initialize-without-context]))

(defn native-init-ga4!
  "Initialize WPP Open Google Analytics 4 tracking"
  [osContext]
  (rf/dispatch [:ga4/initialize-with-os-context osContext]))

(defn track-login!
  "Track user login event"
  [user-data]
  (rf/dispatch [:ga4/track-user-login user-data]))

(defn track-page!
  "Track page view event"
  [page-data]
  (rf/dispatch [:ga4/track-page-view page-data]))

(defn track-virtual-page!
  "Track virtual page view event"
  [virtual-page-data]
  (rf/dispatch [:ga4/track-virtual-page-view virtual-page-data]))

(defn track-interaction!
  "Track user interaction event"
  [interaction-data]
  (rf/dispatch [:ga4/track-user-interaction interaction-data]))

(defn track-feature!
  "Track feature usage event"
  [feature-data]
  (rf/dispatch [:ga4/track-feature-usage feature-data]))
